// API客户端
const API_BASE_URL = '';

// 认证相关工具函数
export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function setAuthToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

export function clearAuth(): void {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_username');
}

export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

// 全局 401 回调（由 App 组件设置）
let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(callback: () => void): void {
  onUnauthorized = callback;
}

export async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log(`API请求: ${options.method || 'GET'} ${url}`);

  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // 自动携带 Authorization header
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`API响应: ${response.status} ${response.statusText}`);

    // Token 过期或无效，自动跳转回登录页
    if (response.status === 401) {
      clearAuth();
      if (onUnauthorized) {
        onUnauthorized();
      }
      throw new Error('认证已过期，请重新登录');
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error(`API错误: ${errorText}`);
      throw new Error(`API请求失败 (${response.status}): ${errorText}`);
    }

    return response.json();
  } catch (error: any) {
    console.error(`API请求异常: ${error.message}`);
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('无法连接到后端服务，请确认后端已启动');
    }
    throw error;
  }
}

// 验证 token 是否有效
export async function verifyToken(): Promise<boolean> {
  try {
    const token = getAuthToken();
    if (!token) return false;

    const response = await fetch('/api/auth/verify', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.ok;
  } catch {
    return false;
  }
}

// 修改密码
export async function changePassword(oldPassword: string, newPassword: string) {
  return fetchAPI('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
  });
}

// 获取股票分析数据
export async function getStockAnalysis(code: string) {
  return fetchAPI(`/api/analyze/stock/${code}`);
}

// 获取股票周线历史数据（含30周均线）
export async function getStockWeeklyData(code: string, weeks: number = 150) {
  return fetchAPI(`/api/stock/${code}/weekly?weeks=${weeks}`);
}

// 获取所有信号
export async function getSignals() {
  const response = await fetchAPI('/api/signals');
  // 后端返回 { signals: [...], total: N }
  return response.signals || [];
}

// 获取市场环境
export async function getMarketContext() {
  return fetchAPI('/api/market/context');
}

// 执行分析
export async function runAnalysis(stockCodes?: string[]) {
  return fetchAPI('/api/analyze/run', {
    method: 'POST',
    body: JSON.stringify({
      stock_codes: stockCodes || [],
      send_notification: false,
      run_ai_analysis: false
    })
  });
}

// 获取实时行情
export async function getRealtimeQuote(code: string) {
  try {
    const data = await fetchAPI(`/api/quote?code=${code}`);
    return data;
  } catch (e) {
    return null;
  }
}

// 获取行情数据源状态
export async function getDataSourceStatus() {
  return fetchAPI('/api/data-source/status');
}

// 股票池扫描 - 启动后台扫描任务
export async function scanMarket(
  maxStocks: number = 0,
  excludeST: boolean = true,
  excludeGEM: boolean = true,
  excludeSTAR: boolean = true,
  generateSignals: boolean = true
) {
  const params = new URLSearchParams({
    exclude_st: excludeST.toString(),
    exclude_gem: excludeGEM.toString(),
    exclude_star: excludeSTAR.toString(),
    generate_signals: generateSignals.toString()
  });
  if (maxStocks > 0) {
    params.append('max_stocks', maxStocks.toString());
  }
  return fetchAPI(`/api/market/scan?${params}`);
}

// 获取扫描进度
export async function getScanStatus() {
  return fetchAPI('/api/market/scan/status');
}

// 获取市场统计
export async function getMarketStatistics() {
  return fetchAPI('/api/market/statistics');
}

// 获取扫描历史记录
export async function getScanHistory(startDate?: string, endDate?: string, limit: number = 100) {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  params.append('limit', limit.toString());
  return fetchAPI(`/api/market/scan/history?${params}`);
}

// 获取最新扫描结果
export async function getLatestScanResults() {
  return fetchAPI('/api/market/scan/latest');
}

// 获取持续强势股（多日出现在第二阶段）
export async function getPersistentPhase2Stocks(minDays: number = 3) {
  return fetchAPI(`/api/market/scan/persistent?min_days=${minDays}`);
}

// 获取系统配置
export async function getConfig() {
  return fetchAPI('/api/config');
}

// 更新股票池
export async function updateStockPool(stockPoolMap: Record<string, string>) {
  return fetchAPI('/api/config/stock-pool', {
    method: 'PUT',
    body: JSON.stringify({ stock_pool_map: stockPoolMap })
  });
}

// 获取ETF规模历史数据
export async function getETFVolume(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const query = params.toString();
  return fetchAPI(`/api/etf/volume${query ? '?' + query : ''}`);
}

// 手动同步最新ETF数据
export async function syncETFData() {
  return fetchAPI('/api/etf/volume/sync', { method: 'POST' });
}

// 历史回填ETF数据
export async function backfillETF(startDate: string, endDate?: string, force?: boolean) {
  const params = new URLSearchParams({ start_date: startDate });
  if (endDate) params.append('end_date', endDate);
  if (force) params.append('force', 'true');
  return fetchAPI(`/api/etf/volume/backfill?${params}`, { method: 'POST' });
}

// 查询回填进度
export async function getBackfillStatus() {
  return fetchAPI('/api/etf/volume/backfill/status');
}

// 检测ETF数据日期断连
export async function getETFDateGaps(maxGapDays: number = 10) {
  return fetchAPI(`/api/etf/volume/gaps?max_gap_days=${maxGapDays}`);
}

// 获取ETF关注列表
export async function getETFWatchlist() {
  return fetchAPI('/api/etf/watchlist');
}

// 添加ETF到关注列表
export async function addETFToWatchlist(secCode: string, secName?: string, color?: string) {
  return fetchAPI('/api/etf/watchlist', {
    method: 'POST',
    body: JSON.stringify({ sec_code: secCode, sec_name: secName || '', color }),
  });
}

// 更新ETF关注列表项
export async function updateETFWatchlistItem(secCode: string, updates: { sec_name?: string; color?: string; enabled?: number; sort_order?: number }) {
  return fetchAPI(`/api/etf/watchlist/${secCode}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// 从关注列表移除ETF
export async function removeETFFromWatchlist(secCode: string) {
  return fetchAPI(`/api/etf/watchlist/${secCode}`, { method: 'DELETE' });
}
